<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import RealEstateBrokerDesktopNavigationItem from './RealEstateBrokerDesktopNavigationItem.vue';
import {
  brokerActiveSection,
  brokerCommissionNavigationItems,
  brokerInvestmentNavigationItems,
  brokerMainNavigationItems,
} from '../composables/realEstateBrokerNavigation';

const props = defineProps({
  commissionSummary: { type: Array, default: () => [] },
  investmentSummary: { type: Object, default: () => ({}) },
  selectedCommission: { type: String, default: '' },
  selectedInvestment: { type: String, default: '' },
});
const emit = defineEmits(['selectCommission', 'selectInvestment']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const collapsed = ref(false);
const commissionsOpen = ref(false);
const investmentsOpen = ref(false);
const activeSection = computed(() => brokerActiveSection(route));
const mainItems = computed(() => brokerMainNavigationItems(t));
const commissionItems = computed(() =>
  brokerCommissionNavigationItems(t, props.commissionSummary)
);
const investmentItems = computed(() =>
  brokerInvestmentNavigationItems(t, props.investmentSummary)
);
const collapseLabel = computed(() =>
  collapsed.value
    ? t('REAL_ESTATE_BROKER_DASHBOARD.DESKTOP_NAV.EXPAND')
    : t('REAL_ESTATE_BROKER_DASHBOARD.DESKTOP_NAV.COLLAPSE')
);

watch(
  activeSection,
  value => {
    commissionsOpen.value = value === 'commission';
    investmentsOpen.value = value === 'investment';
  },
  { immediate: true }
);

const accountParams = () => ({ accountId: route.params.accountId });
const store = (key, value) =>
  window.sessionStorage.setItem(key, JSON.stringify(value));
const navigate = name => router.push({ name, params: accountParams() });
const selectCommission = value => {
  store('real_estate_broker_commission_status', value);
  emit('selectCommission', value);
  if (activeSection.value !== 'commission') {
    router.push({
      name: 'operacao_imobiliaria_corretor',
      params: accountParams(),
      query: { section: 'comissao' },
    });
  }
};
const selectInvestment = value => {
  window.sessionStorage.setItem(
    'real_estate_broker_investment_temperature',
    value
  );
  emit('selectInvestment', value);
  if (activeSection.value !== 'investment') {
    navigate('operacao_imobiliaria_corretor_investimentos');
  }
};
const selectMain = value => {
  if (value === 'commission') {
    commissionsOpen.value = !commissionsOpen.value;
    if (activeSection.value !== 'commission') selectCommission('liberado');
    return;
  }
  if (value === 'investment') {
    investmentsOpen.value = !investmentsOpen.value;
    if (activeSection.value !== 'investment') selectInvestment('');
    return;
  }
  window.sessionStorage.removeItem('real_estate_broker_commission_status');
  window.sessionStorage.removeItem('real_estate_broker_investment_temperature');
  navigate(
    value === 'help'
      ? 'operacao_imobiliaria_corretor_ajuda'
      : 'operacao_imobiliaria_corretor'
  );
};
</script>

<template>
  <aside
    data-testid="broker-desktop-navigation"
    class="sticky top-0 hidden h-[calc(100vh-5rem)] shrink-0 flex-col overflow-hidden border-r border-lais-border-soft bg-white transition-[width] duration-700 md:flex"
    :class="collapsed ? 'w-[106px]' : 'w-[295px]'"
    :aria-label="t('REAL_ESTATE_BROKER_DASHBOARD.DESKTOP_NAV.ARIA')"
  >
    <div
      data-testid="broker-desktop-nav-collapse-bar"
      class="flex h-[60px] shrink-0 items-center border-b border-lais-border-soft"
    >
      <button
        data-testid="broker-desktop-nav-toggle"
        class="flex h-full w-full items-center justify-center rounded-[10px] text-[#01988C] transition hover:bg-[#14B8AA]/[0.04]"
        type="button"
        :aria-expanded="!collapsed"
        :aria-label="collapseLabel"
        @click="collapsed = !collapsed"
      >
        <span
          class="i-lucide-chevrons-left size-[22px]"
          :class="collapsed ? 'rotate-180' : ''"
          aria-hidden="true"
        />
      </button>
    </div>
    <nav
      data-testid="broker-desktop-nav-list"
      class="flex-1 space-y-2 overflow-x-hidden overflow-y-auto p-4"
    >
      <RealEstateBrokerDesktopNavigationItem
        v-for="item in mainItems"
        :key="item.id"
        :active="activeSection === item.id"
        :collapsed="collapsed"
        :commission-items="commissionItems"
        :commissions-open="commissionsOpen"
        :investment-items="investmentItems"
        :investments-open="investmentsOpen"
        :item="item"
        :selected-commission="selectedCommission"
        :selected-investment="selectedInvestment"
        @select="selectMain"
        @select-commission="selectCommission"
        @select-investment="selectInvestment"
      />
    </nav>
  </aside>
</template>
